import { ref, readonly } from 'vue';
import flagsmith from 'flagsmith';
import { defaultMaxSecondaryApplicants } from './const';

export enum FeatureFlag {
  WEBSITE_MAINTENANCE = 'WEBSITE_MAINTENANCE',
  MULTIPLE_APPLICANTS = 'multiple-applicants',
  COMMERCIAL_QUOTE_FLOW = 'commercial-quote-flow',
  PURCHASE_ONLY_LOAN_TYPE = 'purchase-only-loan-type',
  ERHL_PRODUCT = 'erhl-product',
  ERHL_D2C_SUBMISSION = 'erhl-d2c-submission',
  NEGATIVE_GEARING_FY27 = 'negative-gearing-fy27',
  SERVICING_METHODOLOGY_FY27 = 'servicing-methodology-fy27',
  CONTACT_PAGE_REDESIGN = 'contact-page-redesign',
  SIMPLE_AND_ONE_YEAR_FINANCIALS_SELF_EMPLOYED_INCOME = 'js-10203-simple-and-1-year-financials-self-employed-income',
  COMPANY_TRUST_SCENARIO = 'pd-632-company-and-trust-scenario-tool-prefilled',
}

// Module-level reactive state (works without Pinia)
const isInitialized = ref(false);
const isLoading = ref(true);
const hasError = ref(false);

/** The Flagsmith identity (broker id) the current flags were loaded against, if any */
const identity = ref<string | null>(null);

/**
 * Backend-resolved ERHL access for the broker this session acts as, populated by
 * `resolveBrokerIdentity()`.
 *
 * This replaced the `is_erhl_pilot` Flagsmith identity trait (JS-10089): access can now
 * be granted to a whole origin type, which is never pushed to an individual broker's
 * Flagsmith identity, so the backend resolves the broker's own trait over their origin
 * type and answers once.
 *
 * `null` means "not resolved yet", which reads as NO access — the gate stays fail-closed
 * before, and in the absence of, a resolution.
 */
const hasErhlAccess = ref<boolean | null>(null);

/** In-flight initialization promise to prevent concurrent init calls */
let initPromise: Promise<void> | null = null;

const initialize = async (): Promise<void> => {
  // Already initialized - return immediately
  if (isInitialized.value) return;

  // Initialization in progress - await the existing promise
  if (initPromise) return initPromise;

  // Start new initialization
  initPromise = (async () => {
    try {
      await flagsmith.init({
        environmentID: import.meta.env.VITE_FLAGSMITH_CLIENT_KEY || '',
        cacheFlags: false,
      });
      isInitialized.value = true;
    } catch (error) {
      console.error('Failed to initialize Flagsmith:', error);
      hasError.value = true;
    } finally {
      isLoading.value = false;
      initPromise = null;
    }
  })();

  return initPromise;
};

// Initialize immediately on module load
initialize();

/**
 * Clear the current Flagsmith identity. After this, flags fall back to the
 * anonymous/environment-level evaluation so identity-gated features default to
 * OFF until a new broker is identified.
 *
 * NOTE: broker-switching is already handled inside `identify()` (it clears the
 * prior identity before re-identifying), and this SPA currently has no in-app
 * logout flow. This is retained for explicit session teardown and is used
 * internally by `identify()`.
 */
/**
 * Record the backend-resolved ERHL access for the broker this session acts as.
 * Anything other than an explicit boolean resets to `null` (unresolved, closed), so a
 * missing field — e.g. a broker-assistant response that carries none — can never be
 * mistaken for a grant.
 *
 * @param value The `hasErhlAccess` field from the broker or selected-broker response
 */
const setErhlAccess = (value: boolean | null | undefined): void => {
  hasErhlAccess.value = typeof value === 'boolean' ? value : null;
};

/**
 * Reset ERHL access to unresolved. Called whenever the session's broker changes or the
 * identity is torn down, so one broker's answer can never be read for another.
 */
const clearErhlAccess = (): void => {
  hasErhlAccess.value = null;
};

const logout = async (): Promise<void> => {
  // Cleared unconditionally: hasErhlAccess can be set (e.g. by resolveBrokerIdentity)
  // even when identify() itself failed and never recorded an identity, so gating this
  // on identity.value would leave that stale grant in place.
  clearErhlAccess();

  if (!identity.value) return;

  try {
    await flagsmith.logout();
  } catch (error) {
    console.error('Failed to clear Flagsmith identity:', error);
  } finally {
    identity.value = null;
  }
};

/**
 * Identify the current broker to Flagsmith so identity-based segments and
 * BE-pushed traits are evaluated. Re-evaluates flags whenever the broker
 * identity changes (e.g. login -> logout -> different broker login).
 *
 * IMPORTANT: we do NOT send traits from the frontend — the back-end owns
 * trait state and has already pushed it. We only identify by broker id.
 */
const identify = async (brokerId: string | null | undefined): Promise<void> => {
  if (!brokerId) return;

  // Ensure Flagsmith is initialised before identifying.
  await initialize();

  // Same identity already loaded - nothing to do.
  if (identity.value === brokerId) return;

  // Identity changed (e.g. different broker login) - clear the previous
  // identity first so stale identity-gated flags never leak across brokers.
  if (identity.value && identity.value !== brokerId) {
    await logout();
  }

  try {
    await flagsmith.identify(brokerId);
    identity.value = brokerId;
  } catch (error) {
    console.error('Failed to identify broker with Flagsmith:', error);
  }
};

const isEnabled = (flagName: FeatureFlag, defaultValue = false): boolean => {
  if (
    flagName !== FeatureFlag.WEBSITE_MAINTENANCE &&
    import.meta.env.VITE_FLAGSMITH_ENABLE_ALL_FEATURES === 'true' &&
    import.meta.env.MODE === 'development'
  ) {
    return true;
  }

  if (!isInitialized.value) {
    return defaultValue;
  }

  try {
    return flagsmith.hasFeature(flagName);
  } catch {
    return defaultValue;
  }
};

const getMaxSecondaryApplicants = (): number => {
  return isEnabled(FeatureFlag.MULTIPLE_APPLICANTS) ? 5 : defaultMaxSecondaryApplicants;
};

const isCommercialQuoteFlowEnabled = (): boolean => {
  return isEnabled(FeatureFlag.COMMERCIAL_QUOTE_FLOW);
};

const isPurchaseOnlyLoanTypeEnabled = (): boolean => {
  return isEnabled(FeatureFlag.PURCHASE_ONLY_LOAN_TYPE);
};

/** Whether the ERHL product flow is enabled via Flagsmith */
const isErhlProductEnabled = (): boolean => {
  return isEnabled(FeatureFlag.ERHL_PRODUCT);
};

/** Whether the FY27 negative gearing changes are enabled via Flagsmith */
const isNegativeGearingFy27Enabled = (): boolean => {
  return isEnabled(FeatureFlag.NEGATIVE_GEARING_FY27);
};

/** Whether the FY27 servicing methodology changes are enabled via Flagsmith (JS-8450) */
const isServicingMethodologyFy27Enabled = (): boolean => {
  return isEnabled(FeatureFlag.SERVICING_METHODOLOGY_FY27);
};

/**
 * Whether the borrower-entity rollout is enabled via Flagsmith. Platform-wide gate for the
 * company/trust borrower work — the CRM gates its borrower/contact views on the same flag, and
 * here it gates emitting the structured `borrowerGroups` submit payload. When off, submissions
 * fall back to the plain-text `commercialBorrowerData` note alone.
 */
const isContactPageRedesignEnabled = (): boolean => {
  return isEnabled(FeatureFlag.CONTACT_PAGE_REDESIGN);
};

/**
 * JS-10203: whether the Simple self employed / One year financials provided documents are offered
 * on the self-employed income forms. One flag shared with the backend, the CRM and the broker portal.
 */
const isSimpleAndOneYearFinancialsSelfEmployedIncomeEnabled = (): boolean => {
  return isEnabled(FeatureFlag.SIMPLE_AND_ONE_YEAR_FINANCIALS_SELF_EMPLOYED_INCOME);
};

/**
 * Whether the broker this session acts as has ERHL access, as resolved by the backend.
 *
 * Populated by `resolveBrokerIdentity()`; unresolved reads as no access.
 */
const isErhlBrokerAccessEnabled = (): boolean => {
  if (import.meta.env.VITE_FLAGSMITH_ENABLE_ALL_FEATURES === 'true' && import.meta.env.MODE === 'development') {
    return true;
  }

  return hasErhlAccess.value === true;
};

/**
 * Whether the ERHL flow should be available to the current broker.
 *
 * This is the single source of truth for ERHL UI gating. The broker gate is
 * ADDITIVE on top of the existing product gate: BOTH the environment-level
 * `erhl-product` flag AND the broker's backend-resolved `hasErhlAccess` must
 * allow it. Either being off (or absent / unresolved / unauthenticated) hides
 * the ERHL flow, which keeps the default render path closed.
 *
 * Callers must have awaited `resolveBrokerIdentity()` for the session first —
 * reading this before then gets the closed default.
 */
const isErhlEnabled = (): boolean => {
  return isErhlProductEnabled() && isErhlBrokerAccessEnabled();
};

/**
 * Whether the customer-facing (D2C) ERHL journey is available.
 *
 * Gated on BOTH the environment-level `erhl-product` flag and the `erhl-d2c-submission`
 * flag, so turning off ERHL as a whole also closes the D2C journey. Unlike `isErhlEnabled`,
 * there is no broker session, so nothing depends on identity or `hasErhlAccess`.
 *
 * Callers must have awaited `initialize()` first — reading this before then gets the
 * closed default.
 */
const isErhlD2cEnabled = (): boolean => {
  return isErhlProductEnabled() && isEnabled(FeatureFlag.ERHL_D2C_SUBMISSION);
};

/**
 * Composable for feature flags
 * Returns reactive state and helper methods for feature flag management
 */
export const useFeatureFlags = () => {
  return {
    // State (readonly to prevent external mutation)
    isInitialized: readonly(isInitialized),
    isLoading: readonly(isLoading),
    hasError: readonly(hasError),
    identity: readonly(identity),
    hasErhlAccess: readonly(hasErhlAccess),
    // Kept for backward compatibility
    flagsmithInitialized: readonly(isInitialized),
    // Actions
    initialize,
    identify,
    logout,
    setErhlAccess,
    clearErhlAccess,
    isEnabled,
    getMaxSecondaryApplicants,
    isCommercialQuoteFlowEnabled,
    isPurchaseOnlyLoanTypeEnabled,
    isErhlProductEnabled,
    isErhlBrokerAccessEnabled,
    isErhlEnabled,
    isErhlD2cEnabled,
    isNegativeGearingFy27Enabled,
    isServicingMethodologyFy27Enabled,
    isContactPageRedesignEnabled,
    isSimpleAndOneYearFinancialsSelfEmployedIncomeEnabled,
  };
};
